import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Shield, Car, Building2, PartyPopper, Home as HomeIcon,
  HardHat, ParkingCircle, Siren, ClipboardCheck, MoonStar,
  CheckCircle2, MapPin, Phone, ChevronRight, ArrowDown,
} from "lucide-react";
import { HOME } from "@/constants/testIds";
import { IMAGES } from "@/constants/assets";

const SERVICES = [
  { icon: Shield, title: "Professional Security Officers", desc: "Uniformed, trained officers stationed on-site." },
  { icon: Car, title: "Mobile Security Patrol", desc: "Marked patrol vehicles covering multiple locations." },
  { icon: MoonStar, title: "Day & Night Patrol", desc: "Around-the-clock protection tailored to your schedule." },
  { icon: PartyPopper, title: "Event Security", desc: "Private and public event protection with crowd control." },
  { icon: Building2, title: "Commercial Property", desc: "Retail, office, and industrial site security." },
  { icon: HomeIcon, title: "Residential Security", desc: "Community and residential protection services." },
  { icon: HardHat, title: "Construction Site Security", desc: "Prevent theft, vandalism, and unauthorized access." },
  { icon: ParkingCircle, title: "Parking Lot Patrol", desc: "Deter incidents and maintain order in parking areas." },
];

const TICKER = [
  "24/7 Coverage",
  "Licensed & Insured",
  "King County",
  "Pierce County",
  "Mobile Patrol",
  "Event Security",
  "Construction Sites",
  "Emergency Response",
];

// Framer-motion variants
const containerStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const lineReveal = {
  hidden: { y: "110%", opacity: 0 },
  visible: {
    y: "0%",
    opacity: 1,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  },
};
const fadeUp = {
  hidden: { y: 24, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

// Masked line-by-line reveal helper
function MaskedLine({ children, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span variants={lineReveal} className="block">
        {children}
      </motion.span>
    </span>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.3]);

  return (
    <div className="bg-black text-white">
      {/* HERO — kinetic masked reveal + parallax */}
      <section ref={heroRef} className="relative min-h-[94vh] flex items-stretch overflow-hidden">
        {/* Parallax background image */}
        <motion.div
          style={{ y: heroY, scale: heroScale, opacity: heroOpacity }}
          className="absolute inset-0 will-change-transform"
        >
          <img
            src={IMAGES.heroSecurity}
            alt="Marco Polo Security officer overlooking Seattle skyline"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 hero-scrim pointer-events-none" />
        <div className="absolute inset-0 grid-lines-navy opacity-40 pointer-events-none" />

        {/* Entry veil — reveals from top to bottom */}
        <motion.div
          initial={{ scaleY: 1 }}
          animate={{ scaleY: 0 }}
          transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1], delay: 0.15 }}
          style={{ transformOrigin: "top" }}
          className="absolute inset-0 bg-black z-20"
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20 flex items-center">
          <motion.div
            variants={containerStagger}
            initial="hidden"
            animate="visible"
            className="max-w-3xl"
          >
            {/* Status pill */}
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-sm border border-red-600/50 bg-red-600/10">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 red-pulse" />
              <span className="font-condensed text-[11px] font-bold tracking-[0.28em] uppercase text-red-400">
                WA Statewide Security
              </span>
            </motion.div>

            {/* Masked line-by-line title */}
            <h1
              data-testid={HOME.heroTitle}
              className="font-display font-black text-5xl md:text-6xl lg:text-[6.5rem] leading-[0.9] tracking-tight text-white uppercase"
            >
              <MaskedLine>Marco Polo</MaskedLine>
              <MaskedLine>
                <span className="text-white/95">Security</span>
                <span className="text-red-500">.</span>
              </MaskedLine>
            </h1>

            {/* Rule + subheader */}
            <motion.div variants={fadeUp} className="mt-6 flex items-center gap-3">
              <motion.span
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 0.9, ease: [0.76, 0, 0.24, 1] }}
                style={{ transformOrigin: "left" }}
                className="h-px flex-1 max-w-16 bg-red-600 block"
              />
              <p className="font-condensed text-xl md:text-2xl font-semibold uppercase tracking-wider text-white/90">
                Statewide Protection · WA
              </p>
            </motion.div>

            <motion.p variants={fadeUp} className="mt-6 max-w-xl text-base md:text-lg text-slate-300 leading-relaxed">
              Your safety is our priority. We provide professional security officers, mobile patrol, and event
              security services across Washington State — proudly serving King County, Pierce County, and
              surrounding areas.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link
                to="/quote"
                data-testid={HOME.getQuoteBtn}
                className="group inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-100 text-black px-7 py-3.5 rounded-sm font-condensed uppercase tracking-wider font-bold transition-all shine-on-hover"
              >
                Get Quote
                <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/careers"
                data-testid={HOME.applyNowBtn}
                className="group inline-flex items-center justify-center gap-2 bg-transparent hover:bg-white/5 text-white border border-white/40 hover:border-white px-7 py-3.5 rounded-sm font-condensed uppercase tracking-wider font-bold transition-colors backdrop-blur-sm"
              >
                Apply Now
                <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>

            <motion.div variants={fadeUp} className="mt-14 grid grid-cols-3 gap-5 max-w-2xl">
              {[
                { k: "24/7", v: "Coverage", accent: true },
                { k: "WA", v: "Licensed" },
                { k: "2×", v: "County Focus" },
              ].map((s) => (
                <div key={s.v} className="relative pl-4 border-l-2 border-white/20">
                  <span className={`absolute -left-[3px] top-0 w-1 h-6 ${s.accent ? "bg-red-600" : "bg-white"}`} />
                  <div className="font-display font-black text-3xl md:text-4xl text-white leading-none">{s.k}</div>
                  <div className="font-condensed text-[11px] uppercase tracking-[0.2em] text-slate-400 mt-2">{s.v}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        <TickMarker className="top-24 left-3 sm:left-6" />
        <TickMarker className="top-24 right-3 sm:right-6" rotate="rotate-90" />
        <TickMarker className="bottom-16 left-3 sm:left-6" rotate="-rotate-90" />
        <TickMarker className="bottom-16 right-3 sm:right-6" rotate="rotate-180" />

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-500"
        >
          <span className="font-condensed uppercase tracking-[0.3em] text-[10px]">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          >
            <ArrowDown className="w-4 h-4" />
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-white/10 bg-[#0A0A0A] overflow-hidden">
        <div className="flex marquee whitespace-nowrap py-4">
          {[...TICKER, ...TICKER, ...TICKER].map((t, i) => (
            <div key={i} className="flex items-center gap-8 px-8 font-condensed text-lg font-bold uppercase tracking-[0.2em] text-white/70">
              <span>{t}</span>
              <span className="w-1.5 h-1.5 rounded-full bg-red-600" />
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-24 md:py-32 relative">
        <div className="absolute inset-0 grid-lines-navy opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerStagger}
            className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14"
          >
            <div className="max-w-2xl overflow-hidden">
              <motion.p variants={lineReveal} className="font-condensed text-xs font-bold tracking-[0.28em] uppercase text-red-500 mb-3">
                — What We Offer
              </motion.p>
              <h2 className="font-display font-black text-3xl md:text-5xl lg:text-6xl uppercase text-white leading-[1.02]">
                <MaskedLine>Complete Security</MaskedLine>
                <MaskedLine>Solutions<span className="text-red-500">.</span></MaskedLine>
              </h2>
            </div>
            <motion.p variants={fadeUp} className="text-slate-400 leading-relaxed max-w-md">
              From on-site officers to mobile patrol and event protection, we cover every scenario with
              professionalism, discipline, and rapid response.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="group relative bg-[#0A0A0A]/60 border border-white/10 rounded-sm p-6 hover:border-red-500/50 hover:-translate-y-1 transition-all duration-300"
                data-testid={`service-card-${i}`}
              >
                <div className="absolute top-0 left-0 h-0.5 w-0 bg-red-600 group-hover:w-full transition-all duration-500" />
                <div className="w-10 h-10 rounded-sm bg-white/5 border border-white/10 flex items-center justify-center mb-4 group-hover:bg-red-600 group-hover:border-red-600 transition-colors">
                  <s.icon className="w-4 h-4 text-white" />
                </div>
                <h3 className="font-condensed font-bold text-white uppercase tracking-wider text-base">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-14 flex justify-center">
            <Link
              to="/services"
              className="group inline-flex items-center gap-2 font-condensed uppercase tracking-wider text-white text-sm font-bold border-b-2 border-red-600 pb-1 hover:pb-2 transition-all"
              data-testid="home-view-services-link"
            >
              View All Services
              <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* FLEET */}
      <section className="relative py-24 border-t border-white/8 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerStagger}
            className="max-w-2xl mb-12"
          >
            <motion.p variants={lineReveal} className="font-condensed text-xs font-bold tracking-[0.28em] uppercase text-red-500 mb-3">
              — Our Fleet
            </motion.p>
            <h2 className="font-display font-black text-3xl md:text-5xl lg:text-6xl uppercase text-white leading-[1.02]">
              <MaskedLine>Marco Polo Patrol</MaskedLine>
              <MaskedLine>Vehicles<span className="text-red-500">.</span></MaskedLine>
            </h2>
            <motion.p variants={fadeUp} className="mt-6 text-slate-400 leading-relaxed">
              A marked fleet of Ford Explorer patrol SUVs — same livery, same Marco Polo Security branding —
              deployed across King & Pierce County for consistent, visible protection.
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="grid lg:grid-cols-3 gap-4"
          >
            <div className="lg:col-span-2 relative group rounded-sm overflow-hidden border border-white/10">
              <div className="overflow-hidden">
                <motion.img
                  src={IMAGES.fleetGroup}
                  alt="Marco Polo Security fleet of patrol SUVs at building"
                  initial={{ scale: 1.08 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                  className="w-full h-full aspect-[16/9] lg:aspect-auto object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms]"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent pointer-events-none" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
                <div>
                  <p className="font-condensed text-[10px] tracking-[0.28em] uppercase text-red-500 font-bold">Active Fleet</p>
                  <p className="font-display font-black text-white text-2xl md:text-3xl uppercase mt-1">On Duty · Night & Day</p>
                </div>
                <span className="font-condensed text-[10px] tracking-[0.22em] uppercase text-white/80 border border-white/40 px-2 py-1 shrink-0">
                  Ford Explorer · Marked
                </span>
              </div>
            </div>

            <div className="relative group rounded-sm overflow-hidden border border-white/10 bg-white">
              <div className="overflow-hidden">
                <motion.img
                  src={IMAGES.vehicleRear}
                  alt="Marco Polo Security patrol vehicle rear view with Washington plate"
                  initial={{ scale: 1.08 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                  className="w-full h-full aspect-[16/9] lg:aspect-auto object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms]"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent pointer-events-none" />
              <div className="absolute bottom-5 left-5 right-5">
                <p className="font-condensed text-[10px] tracking-[0.28em] uppercase text-red-500 font-bold">Registered</p>
                <p className="font-display font-black text-white text-xl uppercase mt-1">Washington State</p>
              </div>
            </div>
          </motion.div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-slate-400 font-condensed uppercase tracking-widest text-xs">
            <span>Marked · LED Light Bars · Radio-Equipped · Licensed Officers</span>
            <span className="text-red-500">King & Pierce County · WA</span>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="relative py-24 md:py-32 border-t border-white/8 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerStagger}
          >
            <motion.p variants={lineReveal} className="font-condensed text-xs font-bold tracking-[0.28em] uppercase text-red-500 mb-3">— Why Choose Us</motion.p>
            <h2 className="font-display font-black text-3xl md:text-5xl lg:text-6xl uppercase text-white leading-[1.02]">
              <MaskedLine>Trust. Discipline.</MaskedLine>
              <MaskedLine>Quick Action<span className="text-red-500">.</span></MaskedLine>
            </h2>
            <motion.p variants={fadeUp} className="mt-6 text-slate-400 leading-relaxed">
              Every client has different security needs. That's why we build flexible plans based on your
              location, schedule, property type, and level of protection required. Our officers are trained
              to stay alert, professional, and respectful — always.
            </motion.p>

            <motion.div variants={containerStagger} className="mt-8 space-y-4">
              {[
                "Highly-trained, uniformed security officers",
                "Marked mobile patrol vehicles",
                "Rapid emergency response",
                "Flexible day, night, and 24/7 coverage",
                "Custom plans for every property type",
              ].map((b) => (
                <motion.div key={b} variants={fadeUp} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-sm bg-red-600 flex items-center justify-center shrink-0 mt-0.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-slate-200">{b}</span>
                </motion.div>
              ))}
            </motion.div>

            <motion.div variants={fadeUp}>
              <Link
                to="/quote"
                className="mt-10 inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-black px-6 py-3 rounded-sm font-condensed uppercase tracking-wider font-bold transition-colors"
                data-testid="home-why-cta"
              >
                Request a Quote <ChevronRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="aspect-[4/5] rounded-sm overflow-hidden border border-white/10">
              <img
                src={IMAGES.officerInStore}
                alt="Marco Polo Security officer on duty"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Spotlight corner tag */}
            <div className="hidden md:flex absolute -bottom-6 -left-6 w-40 h-40 bg-black border border-red-600/60 rounded-sm items-center justify-center flex-col text-center diag-stripes">
              <span className="font-condensed text-[10px] tracking-[0.28em] uppercase text-red-500 font-bold">Since Day One</span>
              <span className="font-display font-black text-4xl text-white leading-none mt-2">On</span>
              <span className="font-display font-black text-4xl text-white leading-none">Duty.</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-24 md:py-32 border-t border-white/8 overflow-hidden">
        <div className="absolute inset-0 diag-stripes" />
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerStagger}
          className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.p variants={lineReveal} className="font-condensed text-xs font-bold tracking-[0.32em] uppercase text-red-500 mb-4">— Security You Can Trust</motion.p>
          <h2 className="font-display font-black text-4xl md:text-6xl lg:text-7xl uppercase text-white leading-[1]">
            <MaskedLine>Protecting What</MaskedLine>
            <MaskedLine>Matters Most<span className="text-red-500">.</span></MaskedLine>
          </h2>
          <motion.p variants={fadeUp} className="mt-6 text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto">
            Get a tailored security plan today. Talk to our team about your property, event, or business.
          </motion.p>
          <motion.div variants={fadeUp} className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/quote"
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-100 text-black px-8 py-3.5 rounded-sm font-condensed uppercase tracking-wider font-bold transition-colors"
              data-testid="home-final-quote-btn"
            >
              Get Quote <ChevronRight className="w-4 h-4" />
            </Link>
            <a
              href="tel:2067429195"
              className="inline-flex items-center justify-center gap-2 bg-transparent hover:bg-white/5 text-white border border-white/40 hover:border-white px-8 py-3.5 rounded-sm font-condensed uppercase tracking-wider font-bold transition-colors"
              data-testid="home-call-btn"
            >
              <Phone className="w-4 h-4" /> 206-742-9195
            </a>
          </motion.div>
          <motion.div variants={fadeUp} className="mt-10 flex items-center justify-center gap-4 text-slate-400 text-sm">
            <MapPin className="w-4 h-4 text-red-500" />
            <span className="font-condensed uppercase tracking-widest text-xs">Serving King County · Pierce County · Washington</span>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}

function TickMarker({ className = "", rotate = "" }) {
  return (
    <div className={`absolute pointer-events-none z-10 ${className}`}>
      <div className={`w-6 h-6 relative ${rotate}`}>
        <span className="absolute top-0 left-0 w-3 h-0.5 bg-red-500" />
        <span className="absolute top-0 left-0 w-0.5 h-3 bg-red-500" />
      </div>
    </div>
  );
}
